import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Cookie, Settings, BarChart3, Lock, CheckCircle } from 'lucide-react';

export default function CookiePolicy() {
  const lastUpdated = "April 4, 2026";

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0A0A1B 0%, #0D0D1F 100%)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(10, 10, 27, 0.8)', borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">Back to Privluxe</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: '#7C3AED' }} />
            <span className="text-sm" style={{ color: 'rgba(229,231,235,0.6)' }}>Legal</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.2) 0%, rgba(34,211,238,0.2) 100%)', border: '1px solid rgba(34,197,94,0.3)' }}>
            <Cookie className="h-8 w-8" style={{ color: '#22C55E' }} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Cookie Policy</h1>
          <p style={{ color: 'rgba(229,231,235,0.6)' }}>Last updated: {lastUpdated}</p>
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {/* What Are Cookies */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                <Cookie className="h-5 w-5" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">What Are Cookies?</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and provide a better user experience.</p>
              <p>We also use similar technologies like local storage and session storage to provide our services.</p>
            </div>
          </section>

          {/* Types of Cookies */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <Settings className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Types of Cookies We Use</h2>
            </div>
            <div className="space-y-6" style={{ color: 'rgba(229,231,235,0.8)' }}>
              {/* Essential Cookies */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="h-5 w-5" style={{ color: '#22C55E' }} />
                  <h3 className="text-lg font-semibold text-white">Essential Cookies</h3>
                  <span className="px-2 py-0.5 rounded text-xs" style={{ background: 'rgba(34,197,94,0.3)', color: '#22C55E' }}>Required</span>
                </div>
                <p className="mb-3">These cookies are necessary for the platform to function and cannot be disabled.</p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li><strong>Authentication:</strong> Keep you logged in securely</li>
                  <li><strong>Security:</strong> Protect against CSRF attacks and fraud</li>
                  <li><strong>Session Management:</strong> Maintain your active session</li>
                  <li><strong>Load Balancing:</strong> Ensure consistent server connection</li>
                </ul>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="h-5 w-5" style={{ color: '#22D3EE' }} />
                  <h3 className="text-lg font-semibold text-white">Analytics Cookies</h3>
                  <span className="px-2 py-0.5 rounded text-xs" style={{ background: 'rgba(34,211,238,0.3)', color: '#22D3EE' }}>Optional</span>
                </div>
                <p className="mb-3">Help us understand how visitors interact with our platform.</p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li><strong>Google Analytics:</strong> Usage patterns and demographics (anonymized)</li>
                  <li><strong>Microsoft Clarity:</strong> Session recordings and heatmaps</li>
                  <li><strong>Performance Metrics:</strong> Page load times and errors</li>
                </ul>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="h-5 w-5" style={{ color: '#F59E0B' }} />
                  <h3 className="text-lg font-semibold text-white">Marketing Cookies</h3>
                  <span className="px-2 py-0.5 rounded text-xs" style={{ background: 'rgba(245,158,11,0.3)', color: '#F59E0B' }}>Optional</span>
                </div>
                <p className="mb-3">Used to track advertising effectiveness and show relevant ads.</p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li><strong>Meta Pixel:</strong> Measure ad conversions from Facebook/Instagram</li>
                  <li><strong>Google Ads:</strong> Track campaign effectiveness</li>
                </ul>
              </div>

              {/* Preference Cookies */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="h-5 w-5" style={{ color: '#A78BFA' }} />
                  <h3 className="text-lg font-semibold text-white">Preference Cookies</h3>
                  <span className="px-2 py-0.5 rounded text-xs" style={{ background: 'rgba(124,58,237,0.3)', color: '#A78BFA' }}>Optional</span>
                </div>
                <p className="mb-3">Remember your settings and preferences.</p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li><strong>Theme Settings:</strong> Dark/light mode preference</li>
                  <li><strong>Language:</strong> Your preferred language</li>
                  <li><strong>Notification Settings:</strong> Sound and alert preferences</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Managing Cookies */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <CheckCircle className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Managing Your Cookie Preferences</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>You can control cookies through:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Browser Settings:</strong> Most browsers allow you to block or delete cookies. See your browser's help section for instructions.</li>
                <li><strong className="text-white">Opt-Out Tools:</strong> Use tools like the Google Analytics Opt-out Browser Add-on or the Digital Advertising Alliance's opt-out page.</li>
                <li><strong className="text-white">Do Not Track:</strong> We respect browser Do Not Track signals where technically feasible.</li>
              </ul>
              <div className="p-4 rounded-lg mt-4" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <p className="text-sm" style={{ color: '#FCD34D' }}>
                  <strong>Note:</strong> Disabling essential cookies may prevent you from using core features of Privluxe, including logging in and sending messages.
                </p>
              </div>
            </div>
          </section>

          {/* Third-Party Cookies */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <BarChart3 className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Third-Party Services</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>We use the following third-party services that may set cookies:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <th className="text-left py-3 px-2 text-white">Service</th>
                      <th className="text-left py-3 px-2 text-white">Purpose</th>
                      <th className="text-left py-3 px-2 text-white">Privacy Policy</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td className="py-3 px-2">Google Analytics</td>
                      <td className="py-3 px-2">Website analytics</td>
                      <td className="py-3 px-2"><a href="https://policies.google.com/privacy" className="underline" style={{ color: '#22D3EE' }}>View</a></td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td className="py-3 px-2">Microsoft Clarity</td>
                      <td className="py-3 px-2">Session recordings</td>
                      <td className="py-3 px-2"><a href="https://privacy.microsoft.com" className="underline" style={{ color: '#22D3EE' }}>View</a></td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td className="py-3 px-2">Meta Pixel</td>
                      <td className="py-3 px-2">Ad conversion tracking</td>
                      <td className="py-3 px-2"><a href="https://www.facebook.com/privacy/policy" className="underline" style={{ color: '#22D3EE' }}>View</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section>
            <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 className="text-lg font-semibold text-white mb-3">Questions About Cookies?</h3>
              <p style={{ color: 'rgba(229,231,235,0.6)' }}>Contact us at privacy@privluxe.com</p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/privacy" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#A78BFA' }} />
              <span className="text-sm text-white">Privacy Policy</span>
            </Link>
            <Link to="/terms" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#22D3EE' }} />
              <span className="text-sm text-white">Terms & Conditions</span>
            </Link>
            <Link to="/data-deletion" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <Settings className="h-6 w-6 mx-auto mb-2" style={{ color: '#22C55E' }} />
              <span className="text-sm text-white">Data Deletion</span>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p style={{ color: 'rgba(229,231,235,0.4)' }}>© 2026 Privluxe. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
